import { useEffect, useState } from 'react'
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { DialogWrapper } from 'components/dialogWrapper'
import { MediaItem, MediaStatus, useMediaLibrary } from 'hooks/media'
import { mediaUrl } from './helpers'

interface MediaLibraryModalProps {
    isOpen: boolean
    onClose: () => void
    onSelect: (media: MediaItem) => void
}

const PAGE_SIZE = 12

const MediaLibraryModal = ({ isOpen, onClose, onSelect }: MediaLibraryModalProps) => {
    const [search, setSearch] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [status, setStatus] = useState<MediaStatus | ''>('')
    const [pageIndex, setPageIndex] = useState(0)

    useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedSearch(search)
            setPageIndex(0)
        }, 300)
        return () => clearTimeout(t)
    }, [search])

    const { data, isLoading, isError, isFetching } = useMediaLibrary(
        { search: debouncedSearch, status, pageIndex, pageSize: PAGE_SIZE },
        { enabled: isOpen }
    )

    if (!isOpen) return null

    const body = data?.data ?? {}
    const items: MediaItem[] = Array.isArray(body.results) ? body.results : []
    const pages: number = body.pages ?? 1

    return (
        <DialogWrapper
            isOpen={isOpen}
            setIsOpen={(value: any) => {
                const next = typeof value === 'function' ? value(true) : value
                if (!next) onClose()
            }}
            title="Media library"
        >
            <div className="space-y-4 p-2">
                <div className="flex flex-col gap-2 sm:flex-row">
                    <div className="relative flex-1">
                        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by file name, alt text or caption"
                            className="w-full rounded border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm dark:border-gray-600 dark:bg-slate-700"
                        />
                    </div>
                    <select
                        value={status}
                        onChange={(e) => {
                            setStatus(e.target.value as MediaStatus | '')
                            setPageIndex(0)
                        }}
                        className="rounded border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-slate-700"
                    >
                        <option value="">All images</option>
                        <option value="active">Not used yet</option>
                        <option value="used">Used in articles</option>
                    </select>
                </div>

                {isLoading && (
                    <p className="py-10 text-center text-sm text-gray-500">Loading images…</p>
                )}
                {isError && (
                    <p className="py-10 text-center text-sm text-red-500">
                        Could not load the media library. Try again.
                    </p>
                )}
                {!isLoading && !isError && items.length === 0 && (
                    <p className="py-10 text-center text-sm text-gray-500">
                        No images found. Upload one from the editor to add it here.
                    </p>
                )}

                {items.length > 0 && (
                    <ul
                        className={`grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 ${isFetching ? 'opacity-60' : ''
                            }`}
                    >
                        {items.map((item) => (
                            <li key={item.id}>
                                <button
                                    type="button"
                                    onClick={() => onSelect(item)}
                                    className="group w-full overflow-hidden rounded-lg border border-gray-200 bg-white text-left transition hover:border-blue-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-slate-700 dark:bg-slate-800"
                                >
                                    <div className="aspect-[4/3] bg-gray-100 dark:bg-slate-900">
                                        <img
                                            src={mediaUrl(item.id)}
                                            alt={item.alt_text || item.original_name}
                                            loading="lazy"
                                            className="h-full w-full object-cover"
                                        />
                                    </div>
                                    <div className="p-2">
                                        <p className="truncate text-xs font-medium text-gray-800 dark:text-gray-100" title={item.original_name}>
                                            {item.original_name}
                                        </p>
                                        <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                            {item.status === 'used'
                                                ? `Used in ${item.active_article_count ?? 1} article(s)`
                                                : 'Not used yet'}
                                        </p>
                                    </div>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}

                <div className="flex items-center justify-between pt-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                        Page {pageIndex + 1} of {Math.max(pages, 1)}
                    </span>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            disabled={pageIndex === 0}
                            onClick={() => setPageIndex((p) => Math.max(p - 1, 0))}
                            className="rounded bg-gray-200 px-3 py-1.5 text-sm disabled:opacity-50 dark:bg-slate-700"
                        >
                            Previous
                        </button>
                        <button
                            type="button"
                            disabled={pageIndex + 1 >= pages}
                            onClick={() => setPageIndex((p) => p + 1)}
                            className="rounded bg-gray-200 px-3 py-1.5 text-sm disabled:opacity-50 dark:bg-slate-700"
                        >
                            Next
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded bg-gray-300 px-3 py-1.5 text-sm dark:bg-slate-700"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </DialogWrapper>
    )
}

export default MediaLibraryModal